/**
 * A BOX IN A GUIDE FOR WHAT THE SERVER KEEPS (9 Oct 2026).
 *
 * The Portugal guide «AT password» used to print the standard data every
 * client is registered with on the Portal das Finanças — the answer to the
 * secret question included — in a file anyone could download without signing
 * in. Now the guide holds this box: masked until the reader clicks «Show»,
 * and only then is the server asked (/api/guide-secrets), which answers a
 * signed-in person who works the guide's country and nobody else. «Hide»
 * drops the values; nothing is cached or stored in the browser.
 *
 * A value the server does not have yet (its environment variable is unset)
 * reads «Ask your team lead» — never a blank that looks like a bug.
 */
import { useEffect, useRef, useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { readGuideSecret, GuideSecretError, type GuideSecretField } from '@/integrations/guideSecrets';

/* Four languages: Portugal reads this box more than anyone. */
const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

/** The same dots whatever the value — the mask must not tell its length. */
const MASK = '••••••••';

type State =
  | { k: 'hidden' }
  | { k: 'loading' }
  | { k: 'shown'; fields: GuideSecretField[] }
  | { k: 'error'; status: number; msg: string };

/** Copy one value. Its label names it for screen readers — never the value. */
function CopyBtn({ text, label, lang }: { text: string; label: string; lang: Lang }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 1400);
    } catch { /* clipboard blocked by the browser — the text is still selectable */ }
  };
  const verb = done ? T(lang, 'Copied', 'Copiado', 'Copié', 'Copiado') : T(lang, 'Copy', 'Copiar', 'Copier', 'Copiar');
  return (
    <button type="button" className={`gs-copy ${done ? 'done' : ''}`} onClick={() => { void copy(); }} aria-label={`${verb}: ${label}`} title={verb}>
      {done ? <KBIcon name="check" size={12} /> : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
        </svg>
      )}
    </button>
  );
}

export function GuideSecret({ id, title, lang }: { id: string; title: string; lang: Lang }) {
  const [st, setSt] = useState<State>({ k: 'hidden' });
  const ctl = useRef<AbortController | null>(null);
  useEffect(() => () => ctl.current?.abort(), []);

  const show = async () => {
    ctl.current?.abort();
    const c = new AbortController();
    ctl.current = c;
    setSt({ k: 'loading' });
    try {
      const fields = await readGuideSecret(id, c.signal);
      if (!c.signal.aborted) setSt({ k: 'shown', fields });
    } catch (e) {
      if (c.signal.aborted) return;
      const status = e instanceof GuideSecretError ? e.status : 0;
      /* 401/403 are answers, not faults (the server already writes every 403
         down); anything else is logged by friendlyError like the rest. */
      setSt({ k: 'error', status, msg: status === 401 || status === 403 ? '' : friendlyError(e, lang) });
    }
  };
  const hide = () => { ctl.current?.abort(); setSt({ k: 'hidden' }); };

  const ask = T(lang, 'Ask your team lead', 'Pregunta a tu responsable', 'Demandez à votre responsable', 'Pergunte ao seu responsável');
  const why = st.k !== 'error' ? ''
    : st.status === 401 ? T(lang, 'Your session has expired — sign in again to see it.', 'Tu sesión ha caducado: vuelve a entrar para verlo.', 'Votre session a expiré — reconnectez-vous pour le voir.', 'A sua sessão expirou — volte a entrar para o ver.')
    : st.status === 403 ? T(lang, 'Only people who work this country can see it.', 'Solo lo ve quien trabaja en este país.', 'Seules les personnes qui travaillent sur ce pays peuvent le voir.', 'Só o vê quem trabalha neste país.') + ` ${ask}.`
    : st.msg;

  return (
    <section className="gs" aria-label={title}>
      <div className="gs-head">
        <span className="gs-ico" aria-hidden="true"><KBIcon name="key" size={15} /></span>
        <div className="gs-titles">
          <div className="gs-title">{title}</div>
          <div className="gs-sub">
            {T(lang,
              'Kept off the public site — only signed-in people who work this country can see it.',
              'Fuera del sitio público: solo lo ve quien ha iniciado sesión y trabaja en este país.',
              'Hors du site public — seules les personnes connectées qui travaillent sur ce pays peuvent le voir.',
              'Fora do site público — só o vê quem tem sessão iniciada e trabalha neste país.')}
          </div>
        </div>
        {st.k === 'shown' ? (
          <button type="button" className="gs-btn" onClick={hide}>
            <KBIcon name="eye-off" size={14} />{T(lang, 'Hide', 'Ocultar', 'Masquer', 'Ocultar')}
          </button>
        ) : (
          <button type="button" className="gs-btn" onClick={() => { void show(); }} disabled={st.k === 'loading'} aria-busy={st.k === 'loading'}>
            <KBIcon name="eye" size={14} />
            {st.k === 'loading' ? T(lang, 'Loading…', 'Cargando…', 'Chargement…', 'A carregar…') : T(lang, 'Show', 'Mostrar', 'Afficher', 'Mostrar')}
          </button>
        )}
      </div>

      {st.k === 'shown' ? (
        <dl className="gs-rows">
          {st.fields.map((f) => (
            <div className="gs-row" key={f.key}>
              <dt>{f.label}</dt>
              <dd>
                {f.value == null ? (
                  <span className="gs-none">{ask}</span>
                ) : (
                  <>
                    <span className={`gs-val ${f.secret ? 'mono' : ''}`}>{f.value}</span>
                    <CopyBtn text={f.value} label={f.label} lang={lang} />
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : st.k === 'error' ? (
        <div className="gs-err" role="alert">
          <span>{why}</span>
          {st.status !== 401 && st.status !== 403 && (
            <button type="button" className="gs-retry" onClick={() => { void show(); }}>
              {T(lang, 'Try again', 'Reintentar', 'Réessayer', 'Tentar de novo')}
            </button>
          )}
        </div>
      ) : (
        <div className="gs-masked" aria-hidden="true">{MASK}</div>
      )}
    </section>
  );
}
